/**
 * ItemImageUpload.js — Reusable image upload modal for Cost Estimate & PO items.
 *
 * Supports:
 *  - Local file selection
 *  - Paste from clipboard (screenshot / copy-image)
 *  - URL input
 *
 * On success, calls onUpload(url) with the final image URL.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Link, Clipboard, X, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ItemImageUpload({ currentUrl, onUpload, onClose }) {
  const [tab, setTab] = useState('file'); // 'file' | 'url' | 'paste'
  const [url, setUrl] = useState(currentUrl || '');
  const [preview, setPreview] = useState(currentUrl || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  // Listen for clipboard paste events
  useEffect(() => {
    const handler = async (e) => {
      if (tab !== 'paste') return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) await uploadFile(file);
          break;
        }
      }
    };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [tab]); // eslint-disable-line

  const uploadFile = useCallback(async (file) => {
    setUploading(true);
    setError('');

    // Show a local blob preview immediately so the user sees the image right away
    const blobUrl = URL.createObjectURL(file);
    setPreview(blobUrl);

    try {
      const form = new FormData();
      form.append('file', file);
      // Use existing file upload endpoint (returns GCS proxy URL)
      const res = await axios.post(`${API}/files/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Backend returns a relative path like "/api/files/.../view" — prepend base URL
      const rawPath = res.data?.url || res.data?.view_url || '';
      const uploadedUrl = rawPath.startsWith('/')
        ? `${process.env.REACT_APP_BACKEND_URL}${rawPath}`
        : rawPath;

      // Replace blob preview with permanent server URL, then free blob memory
      setPreview(uploadedUrl);
      setUrl(uploadedUrl);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.detail || err.message));
      // Keep blob preview visible so the user still sees what they pasted
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
  };

  const handleUrlConfirm = () => {
    setPreview(url);
  };

  const handleConfirm = () => {
    onUpload(preview || url);
    onClose();
  };

  const handleClear = () => {
    setUrl('');
    setPreview('');
    onUpload('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="rounded-xl shadow-2xl w-full max-w-md mx-4"
        style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid #e5e4df' }}
        >
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" style={{ color: '#c9a84c' }} />
            <span className="font-semibold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#1a1a2e' }}>
              Item Image
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: '#e5e4df' }}>
          {[
            { key: 'file', label: 'Upload File', icon: Upload },
            { key: 'url', label: 'Paste URL', icon: Link },
            { key: 'paste', label: 'Screenshot', icon: Clipboard },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all"
              style={{
                color: tab === key ? '#1a1a2e' : '#9ca3af',
                borderBottom: tab === key ? '2px solid #c9a84c' : '2px solid transparent',
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* File upload */}
          {tab === 'file' && (
            <div>
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-[#c9a84c] transition-colors"
                style={{ borderColor: '#d1d5db' }}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#c9a84c]" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-[#c9a84c] mb-2" />
                    <p className="text-sm text-[#6b7280]">Click to select or drag & drop</p>
                    <p className="text-xs text-[#9ca3af] mt-1">PNG, JPG, WEBP up to 10MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {/* URL input */}
          {tab === 'url' && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider">Image URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/product.jpg"
                  className="flex-1 px-3 py-2 border rounded text-sm"
                  style={{ borderColor: '#e5e4df' }}
                  onKeyDown={e => { if (e.key === 'Enter') handleUrlConfirm(); }}
                />
                <button
                  onClick={handleUrlConfirm}
                  className="px-3 py-2 rounded text-sm font-semibold"
                  style={{ background: '#1a1a2e', color: '#c9a84c' }}
                >
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Paste / screenshot */}
          {tab === 'paste' && (
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center"
              style={{ borderColor: uploading ? '#c9a84c' : '#d1d5db' }}
            >
              {uploading ? (
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#c9a84c]" />
              ) : (
                <>
                  <Clipboard className="w-8 h-8 mx-auto text-[#c9a84c] mb-2" />
                  <p className="text-sm text-[#6b7280]">Press <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Ctrl+V</kbd> to paste screenshot</p>
                  <p className="text-xs text-[#9ca3af] mt-1">Copy an image first (PrintScreen, snipping tool, etc.)</p>
                </>
              )}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider">Preview</label>
              <div className="relative inline-block">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-40 max-w-full rounded border object-contain"
                  style={{ borderColor: '#e5e4df' }}
                  onLoad={() => setError('')}
                  onError={() => setError('Could not load image from URL')}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderTop: '1px solid #e5e4df' }}
        >
          <button
            onClick={handleClear}
            className="text-xs text-[#9ca3af] hover:text-red-500 transition-colors"
          >
            Clear image
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded text-sm border"
              style={{ borderColor: '#e5e4df', color: '#6b7280' }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!preview && !url}
              className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-40"
              style={{ background: '#c9a84c', color: '#1a1a2e' }}
            >
              Use Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
